<template>
    <ElPopover placement="top" width="auto" trigger="hover" :open-delay="300" :show-arrow="false" popper-class="edition-upgrade-usage-popover">
        <div class="flex flex-col gap-2">{{ moduleName }} {{ t(usageDimensionKey, [format(currentUsageAmount), format(maxUsageAmount)]) }}</div>
        <template #reference v-if="isLogin">
            <slot v-if="$slots.default" />
            <div v-else-if="usageHasReady && (editionControl.freemium || premiumShow)" class="inline-flex flex-row items-center bg-[#edf4fc] text-[#495973]" :class="usageModuleClass">
                {{ t('usageAmount.title') }}: <span class="text-primary" :class="{ 'text-red-500': isLimit }">{{ format(currentUsageAmount) }}/{{ format(maxUsageAmount) }}</span>
            </div>
        </template>
    </ElPopover>
</template>

<script setup lang="ts">
import { onBeforeMount, computed, ref } from 'vue';
import { ElPopover } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { useEditionControl } from '@eureka/edition-upgrade/src/composables/use-edition-control';
import { useUsageAmount } from '@eureka/edition-upgrade/src/composables/use-usage-amount';
import type { UsageAmountWhere } from '@eureka/edition-upgrade/src/constants';
import { format } from '@eureka/utils/number-format';
import { cn, en, jp, tw } from '../../infrastructure/locales';
import { isUserLogin } from '@eureka/utils/account';

const leftover = 10;

const props = withDefaults(defineProps<{
    usedWhere: UsageAmountWhere,
    inside: boolean,
}>(), {
    inside: false,
})

const { editionControl, isAirPremium } = useEditionControl();
const { usageAmount, fetchUsageAmount } = useUsageAmount();

const { t } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
});

const isLogin = ref(false);

const currentUsageAmount = computed(() => {
    return usageAmount.value[props.usedWhere]?.current_amount || 0;
})

const maxUsageAmount = computed(() => {
    return usageAmount.value[props.usedWhere]?.max_amount || 0;
})

const usageHasReady = computed(() => {
    return usageAmount.value[props.usedWhere]?.max_amount !== undefined;
})

const usageDimensionKey = computed(() => {
    const dimension = usageAmount.value[props.usedWhere]?.capacity_dimension || 'DIMENSION_YEAR';
    return `usageAmount.${dimension}`;
})

const usageModuleClass = computed(() => {
    return `usage-amount__${props.usedWhere} ${props.inside ? 'inside' : ''}`;
})

const moduleName = computed(() => {
    return t(`usageAmount.${props.usedWhere}`);
})

const isLimit = computed(() => {
    return currentUsageAmount.value >= maxUsageAmount.value;
})

// 付费版，使用剩余量小于等于10才展示用量次数
const premiumShow = computed(() => {
    return isAirPremium.value && (maxUsageAmount.value - currentUsageAmount.value <= leftover);
})

onBeforeMount(async() => {
    fetchUsageAmount();
    isLogin.value = await isUserLogin();
})
</script>

<style lang="scss">
.edition-upgrade-usage-popover {
    font-size: 12px !important;
    line-height: 16px !important;
    padding: 7px 12px !important;
}
.usage-amount__tech_seek {
    @apply px-2 py-1 h-[24px] text-xs gap-1 rounded;
    &.inside {
        @apply mb-4 xl:absolute xl:bottom-[52px] xl:left-[20px] xl:z-10;
    }
}
.usage-amount__tech_fmea {
    @apply mb-4 px-2 py-1 h-[24px] text-xs gap-1 rounded;
    &.inside {
        @apply lg:absolute lg:bottom-[20px] lg:right-[280px] lg:z-10;
    }
}
.usage-amount__technical_research, .usage-amount__tech_mind {
    height: 32px;
    border-radius: 18px 18px 18px 0;
    padding: 6px 16px;
    gap: 8px;
    background: linear-gradient(270deg, rgba(24, 236, 129, 0.05) 0%, rgba(13, 172, 186, 0.05) 53%, rgba(10, 61, 255, 0.05) 100%);
    box-shadow: 0px 0px 4px 0px rgba(7, 100, 233, 0.14), 0px 4px 8px 0px rgba(7, 100, 233, 0.08);
    border: 1px solid #0A3DFF;
    &::before, &::after {
        content: '';
        position: absolute;
        height: 1px;
        width: calc(100% - 30px);
        background: linear-gradient(90deg, #0A3DFF 0%, #0DACBA 11.98%, #18EC81 23.96%, #0DACBA 36.98%, #0A3DFF 48.44%, #0DACBA 61.46%, #18EC81 73.96%, #0DACBA 86.98%, #0A3DFF 100%);
    }
    &::before {
        top: -1px;
        left: 16px;
    }
    &::after {
        bottom: -1px;
        left: 0;
    }
    &.inside {
        @apply absolute top-[14px] left-[120px] z-[999];
    }

}
</style>
